// Route patterns and the helpers that build them, kept together so a link can
// never drift from the route it targets.

import type { ChurchEvent, ChurchService, Launchpad, Playlist } from "./model/launchpad";

/**
 * A service is addressed by its church as well as its own id: the backend
 * requires the churchID to fetch a service, so the URL has to carry it.
 */
export const SERVICE_DETAIL_ROUTE = "/church/:churchID/service/:serviceID";

export function serviceDetailPath(service: Pick<ChurchService, "churchID" | "id">): string {
  return `/church/${encodeURIComponent(service.churchID)}/service/${encodeURIComponent(service.id)}`;
}

/** Events are addressed the same way, for the same reason. */
export const EVENT_DETAIL_ROUTE = "/church/:churchID/event/:eventID";

export function eventDetailPath(event: Pick<ChurchEvent, "churchID" | "id">): string {
  return `/church/${encodeURIComponent(event.churchID)}/event/${encodeURIComponent(event.id)}`;
}

/** Playlists too: the backend fetches a playlist within its church. */
export const PLAYLIST_DETAIL_ROUTE = "/church/:churchID/playlist/:playlistID";

export function playlistDetailPath(playlist: Pick<Playlist, "churchID" | "id">): string {
  return `/church/${encodeURIComponent(playlist.churchID)}/playlist/${encodeURIComponent(playlist.id)}`;
}

/**
 * A church on its own: its Launchpad picker, which forwards to the Launchpad
 * chosen. GET /v1/church/{churchID}/launchpad.
 */
export const CHURCH_ROUTE = "/church/:churchID";

export function churchPath(churchID: string): string {
  return `/church/${encodeURIComponent(churchID)}`;
}

/** A Launchpad by its ids: GET /v1/church/{churchID}/launchpad/{launchpadID}. */
export const LAUNCHPAD_ROUTE = "/church/:churchID/launchpad/:launchpadID";

export function launchpadPath(churchID: string, launchpadID: string): string {
  return `/church/${encodeURIComponent(churchID)}/launchpad/${encodeURIComponent(launchpadID)}`;
}

/**
 * A Launchpad by the short handle its church claimed: GET /v1/launchpad/tag/{tag}.
 * Under /t/ so a tag can never collide with another route.
 */
export const LAUNCHPAD_TAG_ROUTE = "/t/:tag";

export function launchpadTagPath(tag: string): string {
  return `/t/${encodeURIComponent(tag)}`;
}

/** Where a Launchpad lives: its tag when it has one, its ids otherwise. */
export function canonicalLaunchpadPath(launchpad: Pick<Launchpad, "churchID" | "launchpadID" | "tag">): string {
  return launchpad.tag
    ? launchpadTagPath(launchpad.tag)
    : launchpadPath(launchpad.churchID, launchpad.launchpadID);
}

// Dev tools, only in `npm start` (see index.tsx): a picker for loading any
// Launchpad, and the bundled sample for working offline.
export const TEST_ROUTE = "/test";
export const TEST_SAMPLE_ROUTE = "/test/sample";
